import cn from 'classnames';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

import { NumberDisplay } from '@/shared/components/number-display';

interface LastPriceIndicatorProps {
  price: string;
  quoteSymbol?: string;
  pairId?: number;
}

type PriceDirection = 'up' | 'down' | 'neutral';

/**
 * Displays the current last trade price between asks and bids
 * Features:
 * - Color-coded price direction (green up, red down)
 * - Smooth 300-500ms highlight animation on price changes
 * - Automatic fade-out of highlight
 */
export const LastPriceIndicator = memo<LastPriceIndicatorProps>(
  ({ price }) => {
    const [direction, setDirection] = useState<PriceDirection>('neutral');
    const [lastDirection, setLastDirection] = useState<PriceDirection>('neutral'); // Persists after highlight fades
    const [isHighlighted, setIsHighlighted] = useState(false);
    const prevPriceRef = useRef<string>(price);
    const highlightTimeoutRef = useRef<number | null>(null);

    // Detect price changes and determine direction
    useEffect(() => {
      const prevPrice = parseFloat(prevPriceRef.current);
      const currentPrice = parseFloat(price);

      if (isNaN(prevPrice) || isNaN(currentPrice) || prevPrice === currentPrice) {
        return;
      }

      // Determine direction
      const newDirection: PriceDirection = currentPrice > prevPrice ? 'up' : 'down';
      setDirection(newDirection);
      setLastDirection(newDirection); // Save last direction (persists after highlight)
      setIsHighlighted(true);

      // Clear existing timeout
      if (highlightTimeoutRef.current !== null) {
        clearTimeout(highlightTimeoutRef.current);
      }

      // Auto-fade highlight after 400ms (sweet spot between 300-500ms)
      highlightTimeoutRef.current = window.setTimeout(() => {
        setIsHighlighted(false);
        // Don't reset direction - keep lastDirection for persistent color
        highlightTimeoutRef.current = null;
      }, 400);

      // Update previous price
      prevPriceRef.current = price;

      return () => {
        if (highlightTimeoutRef.current !== null) {
          clearTimeout(highlightTimeoutRef.current);
        }
      };
    }, [price]);

    const tokenPriceValue = useMemo(() => {
      const num = Number(price);
      if (!Number.isFinite(num) || num === 0) return '0';
      return price;
    }, [price]);

    const usdPriceValue = useMemo(() => {
      const num = Number(price);
      if (!Number.isFinite(num)) return '0';
      return num;
    }, [price]);

    // Dynamic color classes based on direction (persistent color from lastDirection)
    const priceColorClass = useMemo(() => {
      // Use lastDirection for persistent color, even when not highlighted
      const activeDirection = isHighlighted ? direction : lastDirection;
      if (activeDirection === 'up') return 'text-green-500';
      if (activeDirection === 'down') return 'text-red-500';
      return 'text-zinc-100';
    }, [direction, lastDirection, isHighlighted]);

    const bgColorClass = useMemo(() => {
      if (!isHighlighted) return '';
      if (direction === 'up') return 'bg-green-500/10';
      if (direction === 'down') return 'bg-red-500/10';
      return '';
    }, [direction, isHighlighted]);

    return (
      <div className="border-y border-zinc-800/80 bg-zinc-950">
        <div
          className={`flex items-center font-mono transition-all duration-300 ease-out ${bgColorClass}`}
          style={{ height: '28px' }}
        >
          <span
            className={cn(
              'px-2 text-left text-base font-semibold flex items-center gap-1',
              priceColorClass,
            )}
          >
            <NumberDisplay
              value={tokenPriceValue}
              decimalPlaces={12}
              minDecimalPlaces={0}
              showTooltip
              className="text-inherit"
            />
            <span className="text-xs text-zinc-500 font-normal">
              ={' '}
              <NumberDisplay
                value={usdPriceValue}
                decimalPlaces={8}
                minDecimalPlaces={2}
                nodeLeft="$"
                nodeLeftAfterSign
                showTooltip
                className="text-zinc-500"
              />
            </span>
          </span>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => prevProps.price === nextProps.price,
);

LastPriceIndicator.displayName = 'LastPriceIndicator';
